<template>
    <div class="no-hero g-px g-mb">
        <div class="row-c-nm event-form">
            <h2>{{heading}}</h2>
            <form class="form form--error-handling" enctype="multipart/form-data" @submit.prevent="submit">
                <!-- Slider main container -->
                <div class="swiper-container">
                    <!-- Additional required wrapper  -->
                    <div class="swiper-wrapper">
                        <!-- Slide 1: INTRO -->
                        <div class="swiper-slide" v-if="newEntry">
                            <h3>
                                <slot name="step1-title">Deel het verhaal van jouw held</slot>
                            </h3>
                            <div class="rte mb-6">
                                <slot name="step1-text">
                                    <p>
                                        Reveil vindt het een mooie gedachte: een plek maken waar de levensverhalen van alle vroegere inwoners van onze gemeentes worden opgeslagen. Via dit formulier kan je een verhaal toevoegen aan de Reveil verhalenbank.<br>
                                        We zoeken geen deprimerende teksten of uitgebreide biografieën, maar mooie, inspirerende anekdotes vol menselijkheid. Het hoeft zeker geen grote literatuur te zijn. Met die teksten gaan we aan de slag. Tijdens het herdenkingsmoment op 1 november krijgen deze kleine verhalen een nieuw leven.
                                    </p>
                                </slot>
                            </div>
                            <div class="swiper-slider__btn-row">
                                <span>Stap {{slide}}/{{totalSlides}}</span>
                                <button type="button" @click="nextSlide" class="btn btn--green">Volgende</button>
                            </div>
                        </div>
                        <!-- Slide 2: CONTACT -->
                        <div class="swiper-slide">
                            <h3>
                                <slot name="step2-title">Jouw gegevens</slot>
                            </h3>
                            <div class="rte mb-6">
                                <slot name="step2-text"></slot>
                            </div>
                            <div class="form-field-wrapper">
                                <div :class="{'error': fields.firstName.error }" class="form-group">
                                    <label for="input-first-name">Voornaam <span>*</span></label>
                                    <input id="input-first-name"
                                           name="firstName"
                                           type="text"
                                           v-model="fields.firstName.val"
                                           @keyup="checkErrorReq(fields.firstName)"
                                    >
                                    <span v-if="fields.firstName.error">Gelieve een voornaam in te geven.</span>
                                </div>
                                <div :class="{'error': fields.lastName.error }" class="form-group">
                                    <label for="input-last-name">Familienaam <span>*</span></label>
                                    <input id="input-last-name"
                                           name="lastName"
                                           type="text"
                                           v-model="fields.lastName.val"
                                           @keyup="checkErrorReq(fields.lastName)"
                                    >
                                    <span v-if="fields.lastName.error">Gelieve een familienaam in te geven.</span>
                                </div>
                            </div>
                            <div class="form-field-wrapper">
                                <div :class="{'error': fields.email.error }" class="form-group">
                                    <label for="input-email">E-mailadres <span>*</span></label>
                                    <input id="input-email"
                                           name="email"
                                           type="text"
                                           v-model="fields.email.val"
                                           @keyup="checkErrorMail(fields.email)"
                                    >
                                    <span v-if="fields.email.error">Gelieve een correct e-mailadres in te geven.</span>
                                </div>
                                <div class="form-group">
                                    <label for="input-phone">Telefoonnummer</label>
                                    <input id="input-phone"
                                           name="phone"
                                           type="text"
                                           v-model="fields.phone.val">
                                </div>
                            </div>
                            <div class="swiper-slider__btn-row">
                                <span>Stap {{slide}}/{{totalSlides}}</span>
                                <div>
                                    <button type="button" @click="prevSlide" class="btn btn--rose" v-if="newEntry">Terug</button>
                                    <button type="button" @click="nextSlide" class="btn btn--green">Volgende</button>
                                </div>
                            </div>
                        </div>
                        <!-- Slide 3: HERO -->
                        <div class="swiper-slide">
                            <h3>
                                <slot name="step3-title">Jouw held</slot>
                            </h3>
                            <div class="rte mb-6">
                                <slot name="step3-text">
                                    <p>
                                        Mooie verhalen over kleine helden. Vul hieronder alle gegevens in van jouw held.
                                    </p>
                                </slot>
                            </div>
                            <!-- Name -->
                            <div class="form-field-wrapper">
                                <div :class="{'error': fields.firstNameDeceased.error }" class="form-group">
                                    <label for="input-first-name-deceased">Voornaam <span>*</span></label>
                                    <input id="input-first-name-deceased"
                                           name="firstNameDeceased"
                                           type="text"
                                           v-model="fields.firstNameDeceased.val"
                                           @keyup="checkErrorReq(fields.firstNameDeceased)"
                                    >
                                    <span v-if="fields.firstNameDeceased.error">Gelieve een voornaam in te geven.</span>
                                </div>
                                <div :class="{'error': fields.lastNameDeceased.error }" class="form-group">
                                    <label for="input-last-name-deceased">Familienaam <span>*</span></label>
                                    <input id="input-last-name-deceased"
                                           name="lastNameDeceased"
                                           type="text"
                                           v-model="fields.lastNameDeceased.val"
                                           @keyup="checkErrorReq(fields.lastNameDeceased)"
                                    >
                                    <span v-if="fields.lastNameDeceased.error">Gelieve een familienaam in te geven.</span>
                                </div>
                            </div>
                            <!-- Image -->
                            <div class="form-field-wrapper">
                                <div class="form-group">
                                    <label>Foto</label>
                                    <file-upload :folder-id="asset.folderId" :field-id="asset.fieldId" :element-id="asset.elementId" :original-name="asset.originalName" :url="asset.url" @uploadComplete="onUploadComplete"></file-upload>
                                </div>
                            </div>
                            <!-- Dates -->
                            <div class="form-field-wrapper">
                                <div :class="{'error': fields.dayOfBirth.error }" class="form-group">
                                    <label for="input-day-of-birth">Geboortedatum <span>*</span></label>
                                    <input id="input-day-of-birth"
                                           name="dayOfBirth"
                                           type="text"
                                           v-model="fields.dayOfBirth.val"
                                           @keyup="checkErrorDate(fields.dayOfBirth)"
                                           placeholder="dd/mm/jjjj"
                                    >
                                    <span v-if="fields.dayOfBirth.error">Gelieve een correcte datum in te geven, bv. 01/01/2020.</span>
                                </div>
                                <div :class="{'error': fields.dayOfDeath.error }" class="form-group">
                                    <label for="input-day-of-death">Sterfdatum <span>*</span></label>
                                    <input id="input-day-of-death"
                                           name="dayOfDeath"
                                           type="text"
                                           v-model="fields.dayOfDeath.val"
                                           @keyup="checkErrorDate(fields.dayOfDeath)"
                                           placeholder="dd/mm/jjjj"
                                    >
                                    <span v-if="fields.dayOfDeath.error">Gelieve een correcte datum in te geven, bv. 01/01/2020.</span>
                                </div>
                            </div>
                            <!-- Gender & occupation -->
                            <div class="form-field-wrapper">
                                <div :class="{'error': fields.gender.error }" class="form-group">
                                    <label>Geslacht <span>*</span></label>
                                    <div class="checkbox">
                                        <input hidden
                                               id="input-gender-female"
                                               name="gender"
                                               type="radio"
                                               v-model="fields.gender.val"
                                               value="female"
                                               @change="checkErrorChk(fields.gender)"
                                        >
                                        <label for="input-gender-female">Vrouw</label>
                                    </div>
                                    <div class="checkbox">
                                        <input hidden
                                               id="input-gender-male"
                                               name="gender"
                                               type="radio"
                                               v-model="fields.gender.val"
                                               value="male"
                                               @change="checkErrorChk(fields.gender)"
                                        >
                                        <label for="input-gender-male">Man</label>
                                    </div>
                                    <div class="checkbox">
                                        <input hidden
                                               id="input-gender-neutral"
                                               name="gender"
                                               type="radio"
                                               v-model="fields.gender.val"
                                               value="neutral"
                                               @change="checkErrorChk(fields.gender)"
                                        >
                                        <label for="input-gender-neutral">X</label>
                                    </div>
                                    <span v-if="fields.gender.error">Gelieve een antwoord aan te duiden.</span>
                                </div>
                                <div class="form-group">
                                    <label for="input-occupation">Beroep</label>
                                    <input id="input-occupation"
                                           name="occupation"
                                           type="text"
                                           v-model="fields.occupation.val"
                                    >
                                </div>
                            </div>
                            <!-- Covid -->
                            <div :class="{'error': fields.covid19Victim.error }" class="form-group">
                                <label>Gaat dit verhaal over een Covid-19 slachtoffer? <span>*</span></label>
                                <p class="mb-0">We vragen dit omdat er in sommige gemeentes plannen zijn om specifieke Covid-19 herdenkingen te organiseren.</p>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-covid19-yes"
                                           name="covid19Victim"
                                           type="radio"
                                           v-model="fields.covid19Victim.val"
                                           value="yes"
                                           @change="checkErrorChk(fields.covid19Victim)"
                                    >
                                    <label for="input-covid19-yes">Ja</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-covid19-no"
                                           name="covid19Victim"
                                           type="radio"
                                           v-model="fields.covid19Victim.val"
                                           value="no"
                                           @change="checkErrorChk(fields.covid19Victim)"
                                    >
                                    <label for="input-covid19-no">Nee</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-covid19-dont-know"
                                           name="covid19Victim"
                                           type="radio"
                                           v-model="fields.covid19Victim.val"
                                           value="dontKnow"
                                           @change="checkErrorChk(fields.covid19Victim)"
                                    >
                                    <label for="input-covid19-dont-know">Ik weet dit niet</label>
                                </div>
                                <span v-if="fields.covid19Victim.error">Gelieve een antwoord aan te duiden.</span>
                            </div>
                            <!-- Location -->
                            <div :class="{'error': fields.restingPlace.error }" class="form-group">
                                <label>Rustplaats <span>*</span></label>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-resting-place-home"
                                           name="restingPlace"
                                           type="radio"
                                           value="home"
                                           v-model="fields.restingPlace.val"
                                           @change="checkErrorChk(fields.restingPlace)"
                                    >
                                    <label for="input-resting-place-home">Thuis</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-resting-place-cemetery"
                                           name="restingPlace"
                                           type="radio"
                                           value="cemetery"
                                           v-model="fields.restingPlace.val"
                                           @change="checkErrorChk(fields.restingPlace)"
                                    >
                                    <label for="input-resting-place-cemetery">Begraafplaats</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-resting-place-na"
                                           name="restingPlace"
                                           type="radio"
                                           value="na"
                                           v-model="fields.restingPlace.val"
                                           @change="checkErrorChk(fields.restingPlace)"
                                    >
                                    <label for="input-resting-place-na">Niet van toepassing</label>
                                </div>
                                <span v-if="fields.restingPlace.error">Gelieve een antwoord aan te duiden.</span>
                            </div>
                            <div v-show="fields.restingPlace.val == 'cemetery'">
                                <div class="form-group">
                                    <label for="input-tomb-number">Zerknummer of -locatie</label>
                                    <input id="input-tomb-number"
                                           name="tombNumber"
                                           type="text"
                                           v-model="fields.tombNumber.val"
                                    >
                                </div>
                                <div class="form-field-wrapper">
                                    <div :class="{'error': fields.locationStreet.error }" class="form-group">
                                        <label for="input-location-street">Straat <span>*</span></label>
                                        <input id="input-location-street"
                                                name="locationStreet"
                                                type="text"
                                                v-model="fields.locationStreet.val"
                                                @keyup="checkErrorReq(fields.locationStreet)"
                                        >
                                        <span v-if="fields.locationStreet.error">Gelieve een straat in te geven.</span>
                                    </div>
                                    <div class="form-group">
                                        <div class="w-1/5">
                                            <label for="input-location-nr">Nr</label>
                                            <input id="input-location-nr"
                                                    name="locationNr"
                                                    type="text"
                                                    v-model="fields.locationNr.val"
                                                    style="border-color:#52746A"
                                            >
                                        </div>
                                    </div>
                                </div>
                                <div class="form-field-wrapper">
                                    <div :class="{'error': fields.locationZipcode.error }" class="form-group">
                                        <label for="input-location-zipcode">Postcode <span>*</span></label>
                                        <input id="input-location-zipcode"
                                            name="zipcode"
                                            type="text"
                                            v-model="fields.locationZipcode.val"
                                            @keyup="checkErrorReq(fields.locationZipcode)"
                                        >
                                        <span v-if="fields.locationZipcode.error">Gelieve een postcode in te geven.</span>
                                    </div>
                                    <div :class="{'error': fields.locationCity.error }" class="form-group">
                                        <label for="input-location-city">Gemeente of stad <span>*</span></label>
                                        <input id="input-location-city"
                                            name="locationCity"
                                            type="text"
                                            v-model="fields.locationCity.val"
                                            @keyup="checkErrorReq(fields.locationCity)"
                                        >
                                        <span v-if="fields.locationCity.error">Gelieve een gemeente of stad in te geven.</span>
                                    </div>
                                </div>
                            </div>
                            <div class="swiper-slider__btn-row">
                                <span>Stap {{slide}}/{{totalSlides}}</span>
                                <div>
                                    <button type="button" @click="prevSlide" class="btn btn--rose">Terug</button>
                                    <button type="button" @click="nextSlide" class="btn btn--green">Volgende</button>
                                </div>
                            </div>
                        </div>
                        <!-- Slide 4: STORY -->
                        <div class="swiper-slide">
                            <h3>
                                <slot name="step4-title">Het verhaal van mijn held</slot>
                            </h3>
                            <div class="rte mb-6">
                                <slot name="step4-text"></slot>
                            </div>
                            <div :class="{'error': fields.title.error }" class="form-group">
                                <label for="input-title">Titel <span>*</span></label>
                                <input id="input-title"
                                       name="title"
                                       type="text"
                                       v-model="fields.title.val"
                                       @keyup="checkErrorReq(fields.title)"
                                >
                                <span v-if="fields.title.error">Gelieve een titel in te geven.</span>
                            </div>
                            <div :class="{'error': fields.text.error }" class="form-group">
                                <label>Verhaal <span>*</span></label>
                                <small>ENTER = nieuwe paragraaf, SHIFT+ENTER = nieuwe regel</small>
                                <ckeditor :editor="editor"
                                          v-model="fields.text.val"
                                          :config="editorConfig"
                                          @keyup="checkErrorReq(fields.text)"
                                ></ckeditor>
                                <span v-if="fields.text.error">Gelieve een tekst in te geven.</span>
                            </div>
                            <!-- Consent: consentPublication -->
                            <div :class="{'error': fields.consentPublication.error }" class="form-group">
                                <label>Ik geef de toestemming aan vzw Reveil om dit verhaal op de website van Reveil te publiceren <span>*</span></label>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-publication-true"
                                           name="consentPublication"
                                           type="radio"
                                           value="yes"
                                           v-model="fields.consentPublication.val"
                                           @change="checkErrorChk(fields.consentPublication)"
                                    >
                                    <label for="input-consent-publication-true">Ja</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-publication-false"
                                           name="consentPublication"
                                           type="radio"
                                           value="no"
                                           v-model="fields.consentPublication.val"
                                           @change="checkErrorChk(fields.consentPublication)"
                                    >
                                    <label for="input-consent-publication-false">Nee</label>
                                </div>
                                <span v-if="fields.consentPublication.error">Gelieve een antwoord aan te duiden.</span>
                            </div>
                            <!-- Consent: consentFamily -->
                            <div :class="{'error': fields.consentFamily.error }" class="form-group">
                                <label>Ik heb de toestemming van andere familieleden en betrokkenen om dit verhaal te delen <span>*</span></label>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-family-true"
                                           name="consentFamily"
                                           type="radio"
                                           value="yes"
                                           v-model="fields.consentFamily.val"
                                           @change="checkErrorChk(fields.consentFamily)"
                                    >
                                    <label for="input-consent-family-true">Ja</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-family-false"
                                           name="consentFamily"
                                           type="radio"
                                           value="no"
                                           v-model="fields.consentFamily.val"
                                           @change="checkErrorChk(fields.consentFamily)"
                                    >
                                    <label for="input-consent-family-false">Nee</label>
                                </div>
                                <span v-if="fields.consentFamily.error">Gelieve een antwoord aan te duiden.</span>
                            </div>
                            <!-- Consent: consentPrivacyPolicyAndConditions -->
                            <div :class="{'error': fields.consentPrivacyPolicyAndConditions.error }" class="form-group">
                                <label>Ik ga akkoord met de <a href="/gebruiksvoorwaarden" target="_blank">gebruiksvoorwaarden</a> en de verwerking van persoonsgegevens zoals omschreven in de <a href="/privacyverklaring" target="_blank">privacyverklaring</a> <span>*</span></label>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-policies-true"
                                           name="consentPrivacyPolicyAndConditions"
                                           type="radio"
                                           value="yes"
                                           v-model="fields.consentPrivacyPolicyAndConditions.val"
                                           @change="checkErrorConsent(fields.consentPrivacyPolicyAndConditions)"
                                    >
                                    <label for="input-consent-policies-true">Ja</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-policies-false"
                                           name="consentPrivacyPolicyAndConditions"
                                           type="radio"
                                           value="no"
                                           v-model="fields.consentPrivacyPolicyAndConditions.val"
                                           @change="checkErrorConsent(fields.consentPrivacyPolicyAndConditions)"
                                    >
                                    <label for="input-consent-policies-false">Nee</label>
                                </div>
                                <span v-if="fields.consentPrivacyPolicyAndConditions.error">Gelieve akkoord te gaan.</span>
                            </div>
                            <!-- Consent: consentUploadStory -->
                            <div :class="{'error': fields.consentUploadStory.error }" class="form-group">
                                <label>Ik ben 13 jaar of ouder of ik ben ouder of (wettelijke) voogd van een kind dat jonger is dan 13 jaar, en geef toestemming voor het opladen van het verhaal onder de voorwaarden, inclusief ivm verwerking van persoonsgegevens, omschreven in de <a href="/gebruiksvoorwaarden" target="_blank">gebruiksvoorwaarden</a> en de <a href="/privacyverklaring" target="_blank">privacyverklaring</a> <span>*</span></label>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-upload-true"
                                           name="consentUploadStory"
                                           type="radio"
                                           value="yes"
                                           v-model="fields.consentUploadStory.val"
                                           @change="checkErrorConsent(fields.consentUploadStory)"
                                    >
                                    <label for="input-consent-upload-true">Ja</label>
                                </div>
                                <div class="checkbox">
                                    <input hidden
                                           id="input-consent-upload-false"
                                           name="consentUploadStory"
                                           type="radio"
                                           value="no"
                                           v-model="fields.consentUploadStory.val"
                                           @change="checkErrorConsent(fields.consentUploadStory)"
                                    >
                                    <label for="input-consent-upload-false">Nee</label>
                                </div>
                                <span v-if="fields.consentUploadStory.error">Gelieve akkoord te gaan.</span>
                            </div>
                            <div class="swiper-slider__btn-row">
                                <span>Stap {{slide}}/{{totalSlides}}</span>
                                <div>
                                    <button type="button" @click="prevSlide" class="btn btn--rose">Terug</button>
                                    <button type="submit" class="btn btn--green">Verzenden</button>
                                </div>
                            </div>
                        </div>
                        <!-- Slide 5: SENT -->
                        <div class="swiper-slide">
                            <h3>
                                <slot name="step5-title">Verhaal verstuurd</slot>
                            </h3>
                            <div class="rte mb-6">
                                <slot name="step5-text">
                                    <p>Bedankt!</p>
                                </slot>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Swiper from 'swiper'
import vSelect from 'vue-select'
import CKEditor from '@ckeditor/ckeditor5-vue'
import CKEditorStyle from '@ckeditor/ckeditor5-build-classic'
import FileUpload from './components/FileUpload.vue'
import VueSmoothScroll from 'vue2-smooth-scroll'

window.axios = axios

export default {
    props: {
        entryId: Number,
        firstName: String,
        lastName: String,
        email: String
    },
    components: {
        ckeditor: CKEditor.component,
        vSelect,
        FileUpload,
        VueSmoothScroll
    },
    data() {
        return {
            editor: CKEditorStyle,
            editorConfig: {
                toolbar: ['bold','italic','|','numberedlist','bulletedlist','|','link','|','undo','redo']
            },
            slide: 1,
            totalSlides: 4,
            heading: 'Reveil verhalenbank',
            newEntry: true,
            asset: {
                folderId: 9, // verhalen
                fieldId: 1, // field headerImage
                elementId: null,
                originalName: null,
                url: null
            },
            fields: {
                entryId: {
                    val: false,
                    error: false
                },
                firstName: {
                    val: '',
                    error: false
                },
                lastName: {
                    val: '',
                    error: false
                },
                email: {
                    val: '',
                    error: false
                },
                phone: {
                    val: '',
                    error: false
                },
                firstNameDeceased: {
                    val: '',
                    error: false
                },
                lastNameDeceased: {
                    val: '',
                    error: false
                },
                headerImage: {
                    val: '',
                    error: false
                },
                dayOfBirth: {
                    val: '',
                    error: false
                },
                dayOfDeath: {
                    val: '',
                    error: false
                },
                gender: {
                    val: null,
                    error: false
                },
                occupation: {
                    val: '',
                    error: false
                },
                covid19Victim: {
                    val: null,
                    error: false
                },
                locationStreet: {
                    val: '',
                    error: false
                },
                locationNr: {
                    val: '',
                    error: false
                },
                locationZipcode: {
                    val: '',
                    error: false
                },
                locationCity: {
                    val: '',
                    error: false
                },
                tombNumber: {
                    val: '',
                    error: false
                },
                title: {
                    val: '',
                    error: false
                },
                text: {
                    val: '',
                    error: false
                },
                consentPublication: {
                    val: null,
                    error: false
                },
                consentFamily: {
                    val: null,
                    error: false
                },
                consentPrivacyPolicyAndConditions: {
                    val: null,
                    error: false
                },
                consentUploadStory: {
                    val: null,
                    error: false
                },
                restingPlace: {
                    val: null,
                    error: false
                }
            }
        }
    },
    methods: {
        nextSlide() {
            switch (this.slide) {
                case 2:
                    const firstName = !this.checkRequired(this.fields.firstName);
                    const lastName = !this.checkRequired(this.fields.lastName);
                    const email = !this.checkEmail(this.fields.email);

                    if (firstName && lastName && email) {
                        this.slide += 1;
                        this.swiper.slideNext();
                    }
                    break;

                case 3:
                    const firstNameDeceased = !this.checkRequired(this.fields.firstNameDeceased);
                    const lastNameDeceased = !this.checkRequired(this.fields.lastNameDeceased);
                    const dayOfBirth = !this.checkDate(this.fields.dayOfBirth);
                    const dayOfDeath = !this.checkDate(this.fields.dayOfDeath);
                    const gender = !this.checkCheckboxes(this.fields.gender);
                    const covid19Victim = !this.checkCheckboxes(this.fields.covid19Victim);
                    const restingPlace = !this.checkCheckboxes(this.fields.restingPlace);
                    const locationStreet = this.fields.restingPlace.val == 'cemetery' ? !this.checkRequired(this.fields.locationStreet) : true;
                    const locationCity = this.fields.restingPlace.val == 'cemetery' ? !this.checkRequired(this.fields.locationCity) : true;
                    const locationZipcode = this.fields.restingPlace.val == 'cemetery' ? !this.checkRequired(this.fields.locationZipcode) : true;

                    if (firstNameDeceased && lastNameDeceased && dayOfBirth && dayOfDeath && gender && covid19Victim && restingPlace && locationStreet && locationCity && locationZipcode) {
                        this.slide += 1;
                        this.swiper.slideNext();
                    }
                    break;

                default:
                    this.slide += 1;
                    this.swiper.slideNext();
            }

            this.$smoothScroll({
                scrollTo: document.getElementById("app"),
                updateHistory: false
            })
        },
        prevSlide() {
            this.slide -= 1
            this.swiper.slidePrev()
            this.$smoothScroll({
                scrollTo: document.getElementById("app"),
                updateHistory: false
            })
        },
        checkRequired(field) {
            const bool = field.val === ''
            field.error = bool
            return bool
        },
        checkEmail(field) {
            const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            const bool = re.test(String(field.val).toLowerCase())
            field.error = !bool
            return !bool
        },
        checkDate(field) {
            const re = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
            const bool = re.test(field.val)
            field.error = !bool
            return !bool
        },
        checkCheckboxes(field) {
            const bool = field.val === null
            field.error = bool
            return bool
        },
        checkConsent(field) {
            const bool = field.val == 'yes'
            field.error = !bool
            return !bool
        },
        checkErrorReq(field) {
            if (field.val !== '') field.error = false
        },
        checkErrorMail(field) {
            const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            const bool = re.test(String(field.val).toLowerCase())
            if (bool) field.error = false
        },
        checkErrorDate(field) {
            const re = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
            const bool = re.test(field.val)
            if (bool) field.error = false
        },
        checkErrorChk(field) {
            const bool = field.val !== null
            if (bool) field.error = false
        },
        checkErrorConsent(field) {
            const bool = field.val == 'yes'
            field.error = !bool
        },
        addRow(rows, row) {
            rows.push(row)
        },
        deleteRow(rows, row) {
            var i = rows.indexOf(row)
            if (i > -1) {
                rows.splice(i, 1)
            }
        },
        onUploadComplete(args) {
            console.log(args)
            this.fields.headerImage.val = args.assetId
        },
        getEntry(entryId) {
            window.axios.get('/api/stories/'+entryId+'.json')
                .then(response => {
                    if(response.data) {
                        this.fields.entryId.val = response.data.entryId
                        this.fields.firstName.val = response.data.firstName
                        this.fields.lastName.val = response.data.lastName
                        this.fields.email.val = response.data.email
                        this.fields.phone.val = response.data.phone
                        this.fields.firstNameDeceased.val = response.data.firstNameDeceased
                        this.fields.lastNameDeceased.val = response.data.lastNameDeceased
                        this.fields.headerImage.val = response.data.headerImage
                        this.fields.dayOfBirth.val = response.data.dayOfBirth
                        this.fields.dayOfDeath.val = response.data.dayOfDeath
                        this.fields.gender.val = response.data.gender
                        this.fields.occupation.val = response.data.occupation
                        this.fields.covid19Victim.val = response.data.covid19Victim
                        this.fields.restingPlace.val = response.data.restingPlace
                        this.fields.locationStreet.val = response.data.locationStreet
                        this.fields.locationNr.val = response.data.locationNr
                        this.fields.locationCity.val = response.data.locationCity
                        this.fields.locationZipcode.val = response.data.locationZipcode
                        this.fields.tombNumber.val = response.data.tombNumber
                        this.fields.title.val = response.data.title
                        this.fields.text.val = response.data.text
                        this.fields.consentPublication.val = response.data.consentPublication
                        this.fields.consentFamily.val = response.data.consentFamily
                        if(response.data.headerImage !== null) {
                            this.fields.headerImage.val = response.data.headerImage.assetId
                            this.asset.originalName = response.data.headerImage.filename
                            this.asset.url = response.data.headerImage.url
                        }

                        this.asset.elementId = response.data.entryId
                    }
                })
                .catch(error => console.log(error))
        },
        submit () {

            const title = !this.checkRequired(this.fields.title);
            const text = !this.checkRequired(this.fields.text);
            const consentPublication = !this.checkCheckboxes(this.fields.consentPublication);
            const consentFamily = !this.checkCheckboxes(this.fields.consentFamily);
            const consentPrivacyPolicyAndConditions = !this.checkConsent(this.fields.consentPrivacyPolicyAndConditions);
            const consentUploadStory = !this.checkConsent(this.fields.consentUploadStory);

            if (title && text && consentPublication && consentFamily && consentPrivacyPolicyAndConditions && consentUploadStory) {

                var action = '/actions/reveil-module/stories/save-entry'
                var csrfToken = document.head.querySelector('meta[name="csrf-token"]').content
                var formData = new FormData()

                formData.set('CRAFT_CSRF_TOKEN', csrfToken)
                formData.set('entryId', this.fields.entryId.val)
                formData.set('firstName', this.fields.firstName.val)
                formData.set('lastName', this.fields.lastName.val)
                formData.set('email', this.fields.email.val)
                formData.set('phone', this.fields.phone.val)
                formData.set('firstNameDeceased', this.fields.firstNameDeceased.val)
                formData.set('lastNameDeceased', this.fields.lastNameDeceased.val)
                formData.set('headerImage', this.fields.headerImage.val)
                formData.set('dayOfBirth', this.fields.dayOfBirth.val)
                formData.set('dayOfDeath', this.fields.dayOfDeath.val)
                formData.set('gender', this.fields.gender.val)
                formData.set('occupation', this.fields.occupation.val)
                formData.set('covid19Victim', this.fields.covid19Victim.val)
                formData.set('restingPlace', this.fields.restingPlace.val)
                formData.set('locationStreet', this.fields.locationStreet.val)
                formData.set('locationNr', this.fields.locationNr.val)
                formData.set('locationCity', this.fields.locationCity.val)
                formData.set('locationZipcode', this.fields.locationZipcode.val)
                formData.set('tombNumber', this.fields.tombNumber.val)
                formData.set('title', this.fields.title.val)
                formData.set('text', this.fields.text.val)
                formData.set('consentPublication', this.fields.consentPublication.val)
                formData.set('consentFamily', this.fields.consentFamily.val)
                formData.set('consentPrivacyPolicyAndConditions', this.fields.consentPrivacyPolicyAndConditions.val)
                formData.set('consentUploadStory', this.fields.consentUploadStory.val)

                window.axios({
                    method: 'post',
                    headers: {
                        "Content-Type": 'application/x-www-form-urlencoded',
                    },
                    data: formData,
                    url: action
                }).then((response) => {
                    if (response.data.success == true) {
                        this.fields.entryId = response.data.entryId
                        if (this.newEntry)
                            this.nextSlide()
                        else
                            window.location.href = "/account"
                    }
                }).catch((error) => {
                    console.log(error)
                })
            }
        }
    },
    mounted() {
        this.swiper = new Swiper('.swiper-container', {
            loop: false,
            slidesPerView: 1,
            spaceBetween: 60,
            simulateTouch: false,
            autoHeight: false,
        })
    },
    created() {
        if(this.entryId !== undefined) {
            this.heading = 'Verhaal aanpassen'
            this.newEntry = false
            this.totalSlides = 4
            this.getEntry(this.entryId)
        }
        if(this.firstName !== undefined) {
            this.fields.firstName.val = this.firstName
        }
        if(this.lastName !== undefined) {
            this.fields.lastName.val = this.lastName
        }
        if(this.email !== undefined) {
            this.fields.email.val = this.email
        }
    }
}
</script>

<style>
label a {
    text-decoration: underline;
}
label a:hover {
    text-decoration: none;
    color: text-green;
}
.swiper-slide {
    padding-bottom: 40px;
}
.ck.ck-button .ck.ck-tooltip {
    display: none;
}
.ck.ck-content {
    min-height: 260px;
}
.ck.ck-content p+p {
    @apply mt-4;
}
</style>